/**
 * Внесення фактичного розрахунку від Балансуючої/Агрегуючої групи (після
 * закриття місяця), погодинно на станцію. Три поля на годину: обсяг небалансу
 * (кВт·год, +надлишок/−дефіцит), фактична ціна, сума. Суму можна лишити порожньою
 * — тоді бек/в'юха порахує обсяг×ціна. Порожній рядок при збереженні видаляється.
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import { useFinTheme } from '../theme';

interface Row { hour: number; imbalanceKwh: string; priceUahMwh: string; settlementUah: string }

interface Props {
  open: boolean;
  assetId: string;
  assetName: string;
  date: string;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const empty = (): Row[] => Array.from({ length: 24 }, (_, i) => ({
  hour: i + 1, imbalanceKwh: '', priceUahMwh: '', settlementUah: '',
}));

export default function ActualSettlementDrawer({ open, assetId, assetName, date, canEdit, onClose, onSaved }: Props) {
  const T = useFinTheme();
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>(empty());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null); setRows(empty());
    authFetch(`${API_BASE_URL}/financial/actual-settlement?assetId=${encodeURIComponent(assetId)}&date=${date}`)
      .then((r) => (r.ok ? r.json() : { hours: [] }))
      .then((d: any) => {
        const byHour: Record<number, any> = {};
        (d.hours || []).forEach((h: any) => { byHour[h.hour] = h; });
        setRows(empty().map((r) => {
          const s = byHour[r.hour];
          return s ? {
            hour: r.hour,
            imbalanceKwh: s.imbalanceKwh == null ? '' : String(s.imbalanceKwh),
            priceUahMwh: s.priceUahMwh == null ? '' : String(s.priceUahMwh),
            settlementUah: s.settlementUah == null ? '' : String(s.settlementUah),
          } : r;
        }));
      })
      .catch(() => {});
  }, [open, assetId, date]);

  const set = (hour: number, key: keyof Row, val: string) =>
    setRows((rs) => rs.map((r) => (r.hour === hour ? { ...r, [key]: val } : r)));

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const hours = rows.map((r) => ({
        hour: r.hour,
        imbalanceKwh: r.imbalanceKwh === '' ? null : Number(r.imbalanceKwh),
        priceUahMwh: r.priceUahMwh === '' ? null : Number(r.priceUahMwh),
        settlementUah: r.settlementUah === '' ? null : Number(r.settlementUah),
      }));
      const r = await authFetch(`${API_BASE_URL}/financial/actual-settlement`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, hours }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.message || `HTTP ${r.status}`);
      onSaved();
    } catch (e: any) { setErr(e?.message || t('ak_fin_failed_to_save')); }
    finally { setSaving(false); }
  };

  if (!open) return null;

  const cell = {
    width: '100%', background: T.innerBg, color: T.textMain, border: `1px solid ${T.cardBorder}`,
    borderRadius: 6, padding: '5px 7px', fontSize: 12, outline: 'none', colorScheme: T.inputScheme,
  } as const;
  const th = { padding: '6px 8px', fontSize: 10.5, fontWeight: 700, color: T.textDim, textAlign: 'left' as const };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: T.overlay, zIndex: 60 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(620px, 100%)', zIndex: 61,
        background: T.cardBg, borderLeft: `1px solid ${T.cardBorder}`, display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 30px rgba(0,0,0,0.25)',
      }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
          <div>
            <div className="text-base font-bold" style={{ color: T.textMain }}>{t('ak_fin_actual_settlement_from_the_group')}</div>
            <div className="text-xs" style={{ color: T.textMuted }}>{assetName} · {date.split('-').reverse().join('.')}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer"
            style={{ color: T.textMuted, background: T.innerBg, border: `1px solid ${T.cardBorder}` }}>
            <i className="ri-close-line" style={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="px-5 py-3 overflow-y-auto" style={{ flex: 1 }}>
          {err && (
            <div className="text-xs mb-3 rounded-lg px-3 py-2"
              style={{ background: 'var(--surface-2)', border: '1px solid color-mix(in srgb, var(--err) 35%, transparent)', color: T.errText }}>{err}</div>
          )}
          <div className="text-[11px] mb-2" style={{ color: T.textDim }}>
            {t('ak_fin_imbalance_volume_positive_surplus_nega')}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 44 }}>{t('ak_fin_hr')}</th>
                <th style={th}>{t('ak_fin_imbalance_kwh')}</th>
                <th style={th}>{t('ak_fin_price_uah_mwh')}</th>
                <th style={th}>{t('ak_fin_amount_uah')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.hour}>
                  <td style={{ padding: '3px 8px', fontSize: 12, color: T.textMuted, whiteSpace: 'nowrap' }}>
                    {String(r.hour).padStart(2, '0')}
                  </td>
                  <td style={{ padding: '3px 4px' }}>
                    <input value={r.imbalanceKwh} disabled={!canEdit} inputMode="decimal"
                      onChange={(e) => set(r.hour, 'imbalanceKwh', e.target.value)} style={cell} />
                  </td>
                  <td style={{ padding: '3px 4px' }}>
                    <input value={r.priceUahMwh} disabled={!canEdit} inputMode="decimal"
                      onChange={(e) => set(r.hour, 'priceUahMwh', e.target.value)} style={cell} />
                  </td>
                  <td style={{ padding: '3px 4px' }}>
                    <input value={r.settlementUah} disabled={!canEdit} inputMode="decimal"
                      onChange={(e) => set(r.hour, 'settlementUah', e.target.value)} style={cell} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4" style={{ borderTop: `1px solid ${T.cardBorder}` }}>
          <button onClick={onClose} className="text-sm rounded-lg px-4 py-2 cursor-pointer"
            style={{ color: T.textMain, background: T.innerBg, border: `1px solid ${T.cardBorder}` }}>{t('ak_fin_close')}</button>
          {canEdit && (
            <button onClick={save} disabled={saving}
              className="text-sm rounded-lg px-4 py-2 inline-flex items-center gap-1.5 cursor-pointer"
              style={{ color: T.textMain, background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', opacity: saving ? 0.6 : 1 }}>
              <i className="ri-save-3-line" /> {saving ? t('ak_fin_saving') : t('ak_fin_save')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
